import {
    getPresetBrand,
    normalizeBrand
} from "./generator";
import {
    type CreateGreyTemperature,
    CREATE_DEFAULT_SETTINGS,
    type CreateCornerShape,
    type CreatePreviewKind,
    type CreateReadingSize,
    type CreateSharpness,
    type CreateSettings,
    type CreateThemeKey,
    type CreateDensity,
    type CreatePreset
} from "./settings";

type SettingParam = Exclude<keyof CreateSettings, "brand">;

const BRAND_PARAM = "b";

const SETTING_PARAMS: Record<SettingParam, string> = {
    greyTemperature: "g",
    cornerShape: "c",
    readingSize: "r",
    sharpness: "s",
    themeKey: "t",
    density: "d",
    preview: "v",
    preset: "p"
};

const PARAM_ORDER: Array<SettingParam> = [
    "preset",
    "greyTemperature",
    "themeKey",
    "density",
    "sharpness",
    "cornerShape",
    "preview",
    "readingSize"
];

const READING_SIZE_CODES: Record<CreateReadingSize, string> = {
    readingMediumSize: "medium",
    readingSmallSize: "small",
    readingLargeSize: "large"
};

const PRESETS: Array<CreatePreset> = [
    "nibgat-community",
    "caycore",
    "nibgat",
    "custom"
];

const GREY_TEMPERATURES: Array<CreateGreyTemperature> = [
    "auto",
    "pure",
    "warm",
    "cool"
];

const THEME_KEYS: Array<CreateThemeKey> = [
    "light",
    "dark"
];

const DENSITIES: Array<CreateDensity> = [
    "spacious",
    "compact"
];

const SHARPNESSES: Array<CreateSharpness> = [
    "rounded",
    "sharp",
    "soft"
];

const CORNER_SHAPES: Array<CreateCornerShape> = [
    "squircle",
    "round"
];

const PREVIEW_KINDS: Array<CreatePreviewKind> = [
    "dashboard-01",
    "settings-01",
    "components",
    "login-01",
    "reading",
    "chat-01"
];

const pickValue = <T extends string>(value: string | null, options: Array<T>, fallback: T): T => {
    return options.find((option) => option === value) ?? fallback;
};

const getDefaultBrand = (preset: CreatePreset) => {
    return preset === "custom" ? CREATE_DEFAULT_SETTINGS.brand : getPresetBrand(preset);
};

const encodeValue = (key: SettingParam, settings: CreateSettings) => {
    return key === "readingSize" ? READING_SIZE_CODES[settings.readingSize] : settings[key];
};

export const encodeSettings = (settings: CreateSettings) => {
    const params = new URLSearchParams();

    PARAM_ORDER.forEach((key) => {
        if(settings[key] !== CREATE_DEFAULT_SETTINGS[key]) {
            params.set(SETTING_PARAMS[key], encodeValue(key, settings));
        }

        if(key === "preset") {
            const brand = normalizeBrand(settings.brand);

            if(brand && brand !== normalizeBrand(getDefaultBrand(settings.preset))) {
                params.set(BRAND_PARAM, brand.slice(1));
            }
        }
    });

    return params.toString();
};

export const decodeSettings = (search: string): CreateSettings => {
    const params = new URLSearchParams(search);

    const readingCode = params.get(SETTING_PARAMS.readingSize);

    const readingSize = (Object.keys(READING_SIZE_CODES) as Array<CreateReadingSize>).find((size) => READING_SIZE_CODES[size] === readingCode);

    const preset = pickValue(params.get(SETTING_PARAMS.preset), PRESETS, CREATE_DEFAULT_SETTINGS.preset);

    const brandParam = params.get(BRAND_PARAM);

    const brand = brandParam && /^[0-9a-f]{6}$/i.test(brandParam) ? normalizeBrand(`#${brandParam}`) : null;

    return {
        greyTemperature: pickValue(params.get(SETTING_PARAMS.greyTemperature), GREY_TEMPERATURES, CREATE_DEFAULT_SETTINGS.greyTemperature),
        cornerShape: pickValue(params.get(SETTING_PARAMS.cornerShape), CORNER_SHAPES, CREATE_DEFAULT_SETTINGS.cornerShape),
        sharpness: pickValue(params.get(SETTING_PARAMS.sharpness), SHARPNESSES, CREATE_DEFAULT_SETTINGS.sharpness),
        preview: pickValue(params.get(SETTING_PARAMS.preview), PREVIEW_KINDS, CREATE_DEFAULT_SETTINGS.preview),
        themeKey: pickValue(params.get(SETTING_PARAMS.themeKey), THEME_KEYS, CREATE_DEFAULT_SETTINGS.themeKey),
        density: pickValue(params.get(SETTING_PARAMS.density), DENSITIES, CREATE_DEFAULT_SETTINGS.density),
        readingSize: readingSize ?? CREATE_DEFAULT_SETTINGS.readingSize,
        brand: brand ?? getDefaultBrand(preset),
        preset
    };
};
